import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useGaContext } from "@/hooks/useGaContext";
import { addDays, format, isBefore, parseISO, startOfDay } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
} from "@/components/ui/form";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  CalendarClock, Search, Send, CheckCircle2, XCircle, Clock, Filter,
  User, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ── Types ─────────────────────────────────────────────────────────────────────

interface RenewalEntry {
  clientId: number;
  clientName: string;
  brokerAccountId: string;
  brokerName: string;
  renewalDate: string | null;
  planType: string | null;
  planCount: number;
  outreach: OutreachRecord | null;
}

interface OutreachRecord {
  id: number;
  outreachType: string;
  message: string | null;
  status: string;
  brokerNote: string | null;
  createdAt: string;
}

type SortKey = "renewalDate" | "clientName" | "brokerName" | "outreach";
type SortDir = "asc" | "desc";

// ── Config ────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

const OUTREACH_TYPES = [
  { value: "quoting_support",    label: "Quoting Support" },
  { value: "enrollment_support", label: "Enrollment Support" },
  { value: "renewal_review",     label: "Renewal Review Meeting" },
  { value: "market_check",       label: "Market Check / Rebid" },
  { value: "other",              label: "Other" },
];

const OUTREACH_STATUS: Record<string, { label: string; icon: any; className: string }> = {
  sent:      { label: "Sent",      icon: Send,         className: "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800" },
  accepted:  { label: "Accepted",  icon: CheckCircle2, className: "bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800" },
  declined:  { label: "Declined",  icon: XCircle,      className: "bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800" },
  completed: { label: "Completed", icon: CheckCircle2, className: "bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800" },
};

const TIME_WINDOWS = [
  { value: "30",  label: "Next 30 days" },
  { value: "60",  label: "Next 60 days" },
  { value: "90",  label: "Next 90 days" },
  { value: "180", label: "Next 6 months" },
  { value: "365", label: "Next 12 months" },
];

const outreachSchema = z.object({
  outreachType: z.string().min(1, "Select a support type"),
  message: z.string().max(1500).optional(),
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function urgencyClass(dateStr: string | null) {
  if (!dateStr) return "bg-muted text-muted-foreground border-border";
  const d = parseISO(dateStr);
  const today = startOfDay(new Date());
  if (isBefore(d, addDays(today, 30))) return "bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800";
  if (isBefore(d, addDays(today, 60))) return "bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800";
  return "bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800";
}

function daysAway(dateStr: string | null): number | null {
  if (!dateStr) return null;
  return Math.round((parseISO(dateStr).getTime() - Date.now()) / 86400000);
}

function OutreachBadge({ status }: { status: string }) {
  const cfg = OUTREACH_STATUS[status] || OUTREACH_STATUS.sent;
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={cn("text-xs gap-1 whitespace-nowrap", cfg.className)}>
      <Icon className="w-3 h-3" />{cfg.label}
    </Badge>
  );
}

// ── Outreach Dialog ───────────────────────────────────────────────────────────

function OutreachDialog({ gaId, entry, onClose }: { gaId: string; entry: RenewalEntry; onClose: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<z.infer<typeof outreachSchema>>({
    resolver: zodResolver(outreachSchema),
    defaultValues: { outreachType: entry.outreach?.outreachType ?? "", message: entry.outreach?.message ?? "" },
  });

  const createMutation = useMutation({
    mutationFn: (v: z.infer<typeof outreachSchema>) =>
      apiRequest("POST", `/api/ga/${gaId}/renewal-outreach`, {
        ...v, brokerAccountId: entry.brokerAccountId, clientId: entry.clientId,
        clientName: entry.clientName, renewalDate: entry.renewalDate,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/ga/${gaId}/renewal-calendar`] });
      toast({ title: "Outreach sent", description: `${entry.clientName} has been flagged for follow-up.` });
      onClose();
    },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: (v: { status: string }) =>
      apiRequest("PATCH", `/api/ga/${gaId}/renewal-outreach/${entry.outreach!.id}`, v),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/ga/${gaId}/renewal-calendar`] });
      toast({ title: "Outreach updated" });
      onClose();
    },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const isEditing = !!entry.outreach;
  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Update Outreach" : "Reach Out to Broker"}</DialogTitle>
          <DialogDescription>
            <span className="font-medium">{entry.clientName}</span>
            {entry.renewalDate && ` · Renewal ${format(parseISO(entry.renewalDate), "MMM d, yyyy")}`}
          </DialogDescription>
        </DialogHeader>

        {isEditing ? (
          <div className="space-y-4">
            <div className="rounded-lg bg-muted p-3 space-y-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {OUTREACH_TYPES.find(t => t.value === entry.outreach!.outreachType)?.label}
              </p>
              {entry.outreach!.message && <p className="text-sm text-foreground">{entry.outreach!.message}</p>}
              {entry.outreach!.brokerNote && (
                <>
                  <Separator className="my-2" />
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Broker note</p>
                  <p className="text-sm text-foreground">{entry.outreach!.brokerNote}</p>
                </>
              )}
            </div>
            <div>
              <p className="text-sm font-medium mb-2">Update status</p>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(OUTREACH_STATUS).map(([key, cfg]) => {
                  const Icon = cfg.icon;
                  return (
                    <Button key={key} variant="outline" size="sm"
                      disabled={isPending || entry.outreach!.status === key}
                      className={cn("gap-1.5 justify-start", entry.outreach!.status === key && "border-[#1737A2] text-[#1737A2]")}
                      onClick={() => updateMutation.mutate({ status: key })}>
                      <Icon className="w-3.5 h-3.5" />{cfg.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(v => createMutation.mutate(v))} className="space-y-4">
              <FormField control={form.control} name="outreachType" render={({ field }) => (
                <FormItem>
                  <FormLabel>Type of support to offer</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl><SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger></FormControl>
                    <SelectContent>
                      {OUTREACH_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="message" render={({ field }) => (
                <FormItem>
                  <FormLabel>Message to broker <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
                  <FormControl>
                    <Textarea placeholder="Add context or talking points for the broker…" className="min-h-[100px] resize-none" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
                <Button type="submit" disabled={isPending} className="bg-[#1737A2] hover:bg-[#1737A2]/90">
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                  {isPending ? "Sending…" : "Send outreach"}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Sort header button ────────────────────────────────────────────────────────

function SortHeader({ label, col, sortKey, sortDir, onSort }: {
  label: string; col: SortKey; sortKey: SortKey; sortDir: SortDir;
  onSort: (c: SortKey) => void;
}) {
  const active = sortKey === col;
  const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <button
      className={cn("flex items-center gap-1 text-xs font-semibold uppercase tracking-wide hover:text-foreground transition-colors",
        active ? "text-foreground" : "text-muted-foreground")}
      onClick={() => onSort(col)}>
      {label}<Icon className="w-3 h-3" />
    </button>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function GARenewalCalendar() {
  const { gaId } = useGaContext();
  const [timeWindow, setTimeWindow] = useState("365");
  const [search, setSearch]               = useState("");
  const [brokerSearch, setBrokerSearch]   = useState("");
  const [brokerFilter, setBrokerFilter]   = useState("all");
  const [outreachFilter, setOutreachFilter] = useState("all");
  const [sortKey, setSortKey]             = useState<SortKey>("renewalDate");
  const [sortDir, setSortDir]             = useState<SortDir>("asc");
  const [page, setPage]                   = useState(1);
  const [outreachEntry, setOutreachEntry] = useState<RenewalEntry | null>(null);

  const { data: entries = [], isLoading } = useQuery<RenewalEntry[]>({
    queryKey: [`/api/ga/${gaId}/renewal-calendar?days=${timeWindow}`],
    enabled: !!gaId,
  });

  // Searchable broker list
  const brokers = useMemo(() => {
    const seen = new Set<string>();
    return entries
      .filter(e => { if (seen.has(e.brokerAccountId)) return false; seen.add(e.brokerAccountId); return true; })
      .map(e => ({ id: e.brokerAccountId, name: e.brokerName }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [entries]);

  const filteredBrokers = useMemo(() =>
    brokers.filter(b => b.name.toLowerCase().includes(brokerSearch.toLowerCase())),
    [brokers, brokerSearch]);

  const handleSort = (col: SortKey) => {
    if (sortKey === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(col); setSortDir("asc"); }
    setPage(1);
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return entries.filter(e => {
      if (q && !e.clientName.toLowerCase().includes(q) && !e.brokerName.toLowerCase().includes(q)) return false;
      if (brokerFilter !== "all" && e.brokerAccountId !== brokerFilter) return false;
      if (outreachFilter === "contacted"     && !e.outreach)                          return false;
      if (outreachFilter === "not_contacted" && e.outreach)                           return false;
      if (outreachFilter === "accepted"      && e.outreach?.status !== "accepted")    return false;
      return true;
    });
  }, [entries, search, brokerFilter, outreachFilter]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortKey === "renewalDate") {
        const da = a.renewalDate ? parseISO(a.renewalDate).getTime() : Infinity;
        const db = b.renewalDate ? parseISO(b.renewalDate).getTime() : Infinity;
        cmp = da - db;
      } else if (sortKey === "clientName") {
        cmp = a.clientName.localeCompare(b.clientName);
      } else if (sortKey === "brokerName") {
        cmp = a.brokerName.localeCompare(b.brokerName);
      } else if (sortKey === "outreach") {
        cmp = (a.outreach ? 1 : 0) - (b.outreach ? 1 : 0);
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paginated  = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to page 1 whenever filters change
  const setFilter = (fn: () => void) => { fn(); setPage(1); };

  // Stats
  const urgentCount   = entries.filter(e => e.renewalDate && isBefore(parseISO(e.renewalDate), addDays(new Date(), 30))).length;
  const outreachCount = entries.filter(e => e.outreach).length;
  const acceptedCount = entries.filter(e => e.outreach?.status === "accepted").length;

  return (
    <div className="p-6 space-y-5">
      {outreachEntry && (
        <OutreachDialog gaId={gaId!} entry={outreachEntry} onClose={() => setOutreachEntry(null)} />
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Renewal Calendar</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Track upcoming renewals across your broker network and proactively offer support.
        </p>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total Renewals", value: entries.length,  color: "text-foreground" },
          { label: "Due in 30 Days", value: urgentCount,     color: "text-red-600 dark:text-red-400" },
          { label: "Outreach Sent",  value: outreachCount,   color: "text-blue-600 dark:text-blue-400" },
          { label: "Accepted",       value: acceptedCount,   color: "text-emerald-600 dark:text-emerald-400" },
        ].map(({ label, value, color }) => (
          <div key={label} className="rounded-lg border border-border bg-card px-4 py-3">
            <p className={cn("text-2xl font-bold", color)}>{value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search client or broker…" className="pl-8 h-9"
            value={search} onChange={e => setFilter(() => setSearch(e.target.value))} />
        </div>

        {/* Time window */}
        <Select value={timeWindow} onValueChange={v => setFilter(() => setTimeWindow(v))}>
          <SelectTrigger className="w-[155px] h-9">
            <CalendarClock className="w-4 h-4 mr-1.5 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIME_WINDOWS.map(w => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
          </SelectContent>
        </Select>

        {/* Broker — searchable */}
        <Select value={brokerFilter} onValueChange={v => setFilter(() => setBrokerFilter(v))}>
          <SelectTrigger className="w-[185px] h-9">
            <User className="w-4 h-4 mr-1.5 text-muted-foreground" />
            <SelectValue placeholder="All brokers">
              {brokerFilter === "all" ? "All brokers" : brokers.find(b => b.id === brokerFilter)?.name ?? "All brokers"}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <div className="px-2 pb-1 pt-1 sticky top-0 bg-popover z-10">
              <Input
                placeholder="Search brokers…"
                className="h-7 text-xs"
                value={brokerSearch}
                onChange={e => setBrokerSearch(e.target.value)}
                onKeyDown={e => e.stopPropagation()}
              />
            </div>
            <SelectItem value="all">All brokers ({brokers.length})</SelectItem>
            {filteredBrokers.length === 0 && (
              <div className="px-3 py-2 text-xs text-muted-foreground">No brokers match</div>
            )}
            {filteredBrokers.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>

        {/* Outreach filter */}
        <Select value={outreachFilter} onValueChange={v => setFilter(() => setOutreachFilter(v))}>
          <SelectTrigger className="w-[165px] h-9">
            <Filter className="w-4 h-4 mr-1.5 text-muted-foreground" />
            <SelectValue placeholder="All outreach" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All outreach</SelectItem>
            <SelectItem value="not_contacted">Not contacted</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
          </SelectContent>
        </Select>

        {/* Result count */}
        {filtered.length !== entries.length && (
          <span className="text-xs text-muted-foreground ml-1">
            {filtered.length} of {entries.length} shown
          </span>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-1">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="animate-pulse h-11 bg-muted rounded" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-border rounded-xl">
          <CalendarClock className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No renewals found</p>
          <p className="text-xs text-muted-foreground mt-1">
            {search || brokerFilter !== "all" || outreachFilter !== "all"
              ? "Try adjusting your filters."
              : "Client plan renewal dates will appear here once set."}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-[1fr_1fr_160px_130px_90px] gap-0 bg-muted border-b border-border px-4 py-2">
            <SortHeader label="Client"       col="clientName"  sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
            <SortHeader label="Broker"       col="brokerName"  sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
            <SortHeader label="Renewal Date" col="renewalDate" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
            <SortHeader label="Outreach"     col="outreach"    sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
            <span />
          </div>

          {/* Rows */}
          <div className="divide-y divide-border">
            {paginated.map((entry) => {
              const days = daysAway(entry.renewalDate);
              const dateLabel = entry.renewalDate
                ? format(parseISO(entry.renewalDate), "MMM d, yyyy")
                : "—";
              return (
                <div
                  key={`${entry.clientId}-${entry.brokerAccountId}`}
                  className="grid grid-cols-[1fr_1fr_160px_130px_90px] gap-0 items-center px-4 py-2.5 hover:bg-muted/50 transition-colors"
                >
                  {/* Client */}
                  <div className="min-w-0 pr-3">
                    <p className="text-sm font-medium text-foreground truncate">{entry.clientName}</p>
                    {entry.planType && (
                      <p className="text-xs text-muted-foreground truncate">
                        {entry.planType}{entry.planCount > 1 && ` +${entry.planCount - 1} more`}
                      </p>
                    )}
                  </div>

                  {/* Broker */}
                  <div className="min-w-0 pr-3">
                    <p className="text-sm text-foreground truncate">{entry.brokerName}</p>
                  </div>

                  {/* Renewal date */}
                  <div className="pr-3">
                    <Badge variant="outline" className={cn("text-xs whitespace-nowrap", urgencyClass(entry.renewalDate))}>
                      {dateLabel}{days !== null && ` · ${days}d`}
                    </Badge>
                  </div>

                  {/* Outreach status */}
                  <div className="pr-3">
                    {entry.outreach
                      ? <OutreachBadge status={entry.outreach.status} />
                      : <span className="text-xs text-muted-foreground">—</span>}
                  </div>

                  {/* Action */}
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant={entry.outreach ? "outline" : "default"}
                      className={cn("h-7 text-xs gap-1", !entry.outreach && "bg-[#1737A2] hover:bg-[#1737A2]/90")}
                      onClick={() => setOutreachEntry(entry)}
                    >
                      {entry.outreach
                        ? <><Clock className="w-3 h-3" />Manage</>
                        : <><Send className="w-3 h-3" />Reach Out</>}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination footer */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted">
              <p className="text-xs text-muted-foreground">
                Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} of {sorted.length}
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-7 w-7 p-0"
                  disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {/* Page number pills — show up to 7 */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                  .reduce<(number | "…")[]>((acc, p, i, arr) => {
                    if (i > 0 && (p as number) - (arr[i - 1] as number) > 1) acc.push("…");
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === "…" ? (
                      <span key={`ellipsis-${i}`} className="text-xs text-muted-foreground px-1">…</span>
                    ) : (
                      <Button key={p} variant={page === p ? "default" : "outline"} size="sm"
                        className={cn("h-7 w-7 p-0 text-xs", page === p && "bg-[#1737A2] hover:bg-[#1737A2]/90")}
                        onClick={() => setPage(p as number)}>
                        {p}
                      </Button>
                    )
                  )}
                <Button variant="outline" size="sm" className="h-7 w-7 p-0"
                  disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
